import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren
} from "react";
import type { TransactionTrackingDeliveryCode } from "features/shipmentTracking/types";

type ContextValue = {
    readonly open: boolean;
    readonly resource: TransactionTrackingDeliveryCode | undefined;
    readonly openView: (resource: TransactionTrackingDeliveryCode) => void;
    readonly onClose: () => void;
};

const ShipmentTrackingContext = createContext<ContextValue>({} as ContextValue);

const ShipmentTrackingProvider = ({ children }: PropsWithChildren) => {
    const [open, setOpen] = useState(false);
    const [resource, setResource] = useState<TransactionTrackingDeliveryCode | undefined>();
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const openView = useCallback((next: TransactionTrackingDeliveryCode) => {
        clearTimeout(timeoutRef.current);
        setResource(next);
        setOpen(true);
    }, []);

    const onClose = useCallback(() => {
        setOpen(false);
        timeoutRef.current = setTimeout(() => setResource(undefined), 300);
    }, []);

    const value = useMemo<ContextValue>(() => ({
        open,
        resource,
        openView,
        onClose
    }), [open, resource, openView, onClose]);

    return (
        <ShipmentTrackingContext.Provider value={value}>
            {children}
        </ShipmentTrackingContext.Provider>
    );
};

export const useShipmentTrackingContext = () => useContext(ShipmentTrackingContext);

export default ShipmentTrackingProvider;
